// Word-start search over a people directory. The webhook has a twin at
// gaintime-bot-webhook/services/contacts/contactSearch.js so the picker and GT Assistant
// rank people the same way.

const RECENCY_HALF_LIFE_DAYS = 30;
const DAY_MS = 24 * 60 * 60 * 1000;
const FILLER_WORDS = new Set(["from", "at", "of", "the", "in", "with"]);

function words(value, pattern) {
  return String(value || "").toLowerCase().split(pattern).filter(Boolean);
}

export function contactTokens(contact) {
  const email = String(contact.email || "").toLowerCase();
  const [local = "", domain = ""] = email.split("@");
  return {
    name: words(contact.name, /\s+/),
    email,
    local: words(local, /[._\-+0-9]+/),
    company: words(domain.split(".")[0], /-/),
  };
}

// Strength of the best way one query word matches, or 0.
function matchWord(word, tokens) {
  if (tokens.name[0]?.startsWith(word)) return 3;
  if (tokens.name.slice(1).some((part) => part.startsWith(word))) return 2.6;
  if (tokens.email.startsWith(word)) return 2.2;
  if (tokens.local.some((part) => part.startsWith(word))) return 1.8;
  if (tokens.company.some((part) => part.startsWith(word))) return 1.4;
  return 0;
}

export function queryWords(query) {
  return words(query, /[\s,]+/).filter((word) => !FILLER_WORDS.has(word));
}

function activityScore(contact, nowMs) {
  const seen = contact.seen || 0;
  const recency = contact.lastSeenMs
    ? Math.pow(0.5, Math.max(0, nowMs - contact.lastSeenMs) / (RECENCY_HALF_LIFE_DAYS * DAY_MS))
    : 0;
  return Math.log(1 + seen) * 0.6 + recency + (contact.internal ? 0.3 : 0);
}

/**
 * Returns matches best first as { contact, match, score }. Every query word
 * must start a name word, an email part, or the company.
 */
export function searchContacts(contacts, query, { limit = 8, nowMs = Date.now() } = {}) {
  const terms = queryWords(query);
  if (!terms.length) return [];

  const results = [];
  for (const contact of contacts || []) {
    const tokens = contactTokens(contact);
    let match = 0;
    for (const term of terms) {
      const strength = matchWord(term, tokens);
      if (!strength) {
        match = 0;
        break;
      }
      match += strength;
    }
    if (match) results.push({ contact, match, score: match + activityScore(contact, nowMs) });
  }

  return results.sort((a, b) => b.score - a.score).slice(0, limit);
}

// Most active people first, for lists shown before the user types.
export function rankContacts(contacts, { nowMs = Date.now() } = {}) {
  return [...(contacts || [])].sort((a, b) => activityScore(b, nowMs) - activityScore(a, nowMs));
}
